import { gql } from '@apollo/client';

/**
 * Amazon device coupons — the third tab under Devices Fulfillment. A $10-off Amazon claim code
 * PRE-GENERATED in Seller Central and allocated from a pool
 * (see apps/api/src/app/amazon-coupons/amazon-coupons.service.ts).
 *
 * Nothing here mints: Amazon publishes no API that creates a promo code, so a human imports a CSV
 * and this hands codes out of it. That is why there is a pool query and no quote query.
 *
 * Operation names match their GraphQL field names so `refetchQueries: ['amazonCoupons']` resolves.
 */

const AMAZON_COUPON_FIELDS = `
  code
  promotionId
  marketplace
  sku
  deviceName
  asin
  discountAmount
  currency
  campaignEndsAt
  customerEmail
  reason
  createdBy
  createdAt
  expiresAt
  used
  usedAt
  promotionRedemptionCount
  promotionReconciledAt
  promotionAllocatedCount
  status
`;

const AMAZON_PROMOTION_FIELDS = `
  id
  marketplace
  sku
  asin
  campaignId
  campaignEndsAt
  discountAmount
  currency
  createdBy
  createdAt
  available
  total
  groupCount
  live
`;

const AMAZON_CODE_GROUP_FIELDS = `
  id
  promotionId
  sourceFilename
  importedBy
  importedAt
  available
  total
`;

/**
 * The ledger — ALLOCATED codes only. filter.status = ACTIVE | USED | EXPIRED (derived from the
 * campaign's end date); filter.search spans code/customer/reason/issuer/SKU.
 *
 * `campaignEndsAt` is selected on every row deliberately: it is the only expiry Amazon enforces,
 * and the table must render it beside the advisory `expiresAt` or a staffer will read ours as real.
 */
export const AMAZON_COUPONS = gql`
  query amazonCoupons($filter: AmazonCouponFilterInput, $pagination: PaginationInput) {
    amazonCoupons(filter: $filter, pagination: $pagination) {
      items { ${AMAZON_COUPON_FIELDS} }
      total
    }
  }
`;

/** What is left to hand out, per marketplace, across LIVE campaigns only. */
export const AMAZON_COUPON_POOLS = gql`
  query amazonCouponPools {
    amazonCouponPools {
      marketplace
      sku
      asin
      discountAmount
      currency
      available
      total
      campaignEndsAt
    }
  }
`;

/**
 * Promotions with their live counts. ROOT-only.
 *
 * The import dialog reads this FIRST: a top-up attaches a code group to one of these, it does not
 * create another promotion and wait 4-5 hours for activation.
 */
export const AMAZON_COUPON_PROMOTIONS = gql`
  query amazonCouponPromotions {
    amazonCouponPromotions { ${AMAZON_PROMOTION_FIELDS} }
  }
`;

/** Imported code groups — which CSV, who, when. ROOT-only. */
export const AMAZON_COUPON_CODE_GROUPS = gql`
  query amazonCouponCodeGroups {
    amazonCouponCodeGroups { ${AMAZON_CODE_GROUP_FIELDS} }
  }
`;

/**
 * Hand one pooled code to a customer. Note what is NOT sent: no code (the server pops one), no
 * expiry (30 days server-side), no operator (taken from the caller).
 */
export const ALLOCATE_AMAZON_COUPON = gql`
  mutation allocateAmazonCoupon($input: AllocateAmazonCouponInput!) {
    allocateAmazonCoupon(input: $input) { ${AMAZON_COUPON_FIELDS} }
  }
`;

/** Register a promotion — the rare 4-5 hour path. */
export const REGISTER_AMAZON_COUPON_PROMOTION = gql`
  mutation registerAmazonCouponPromotion($input: RegisterAmazonCouponPromotionInput!) {
    registerAmazonCouponPromotion(input: $input) { ${AMAZON_PROMOTION_FIELDS} }
  }
`;

/**
 * Attach a code group to an existing promotion — the routine path. The BROWSER parses the CSV;
 * the API only ever sees codes.
 */
export const IMPORT_AMAZON_COUPON_CODES = gql`
  mutation importAmazonCouponCodes($input: ImportAmazonCouponCodesInput!) {
    importAmazonCouponCodes(input: $input) { ${AMAZON_CODE_GROUP_FIELDS} }
  }
`;

/** Run the redemption reconcile now rather than waiting for the daily pass. ROOT-only. */
export const RECONCILE_AMAZON_COUPONS = gql`
  mutation reconcileAmazonCoupons {
    reconcileAmazonCoupons
  }
`;
